import {Component, inject, OnInit, input, signal, computed, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatCardModule} from "@angular/material/card"
import {MatCheckboxModule} from "@angular/material/checkbox"
import {MatDialog} from "@angular/material/dialog"
import {MatIconModule} from "@angular/material/icon"
import {MatMenuModule} from "@angular/material/menu"
import {MatTooltipModule} from "@angular/material/tooltip"
import {RouterLink} from "@angular/router"
import {FittingSchema, MutationUpdateScanJobInput, ScanJobState} from "@generated"
import {ScanJobDialogComponent} from "@app/textures/texture-group-details/scan-job-dialog/scan-job-dialog.component"
import {
    CloneScanJobForRefittingGQL,
    ScanJobsForTextureSetGQL,
    ScanJobUpdateGQL,
    ScanJobFragment,
} from "@app/textures/texture-group-details/scan-job/scan-job.generated"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {DurationFromMillisecondsPipe} from "@common/pipes/duration/duration.pipe"
import {FormatDatePipe} from "@common/pipes/format-date/format-date.pipe"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {RefreshService} from "@common/services/refresh/refresh.service"
import {Enums} from "@enums"
import {Labels} from "@labels"
import {FilesService} from "@common/services/files/files.service"
import {StateLabelComponent} from "@platform/components/shared/state-label/state-label.component"
import {LoadingSpinnerIconComponent} from "@app/common/components/progress"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DurationFromMillisecondsPipe,
        FormsModule,
        MatCardModule,
        MatCheckboxModule,
        MatIconModule,
        MatMenuModule,
        MatTooltipModule,
        RouterLink,
        StateLabelComponent,
        FormatDatePipe,
        LoadingSpinnerIconComponent,
    ],
    selector: "cm-scan-job",
    styleUrls: ["./scan-job.component.scss"],
    templateUrl: "./scan-job.component.html",
})
export class ScanJobComponent implements OnInit {
    readonly $textureSetId = input<string>(undefined, {alias: "textureSetId"})

    readonly $scanJob = signal<ScanJobFragment | undefined>(undefined)
    readonly $activeSubJobsCount = computed(() => this.$scanJob()?.activeSubJobsCount)
    readonly $completedSubJobsCount = computed(() => this.$scanJob()?.completedSubJobsCount)
    readonly $scanJobDuration = computed(() => {
        const scanJob = this.$scanJob()
        if (!scanJob?.started || !scanJob?.completed) {
            return undefined
        }
        return new Date(scanJob.completed).getTime() - new Date(scanJob.started).getTime()
    })

    readonly $canClone = computed(() => {
        const scanJob = this.$scanJob()
        if (!scanJob) {
            return false
        }
        const cloneableStates: ScanJobState[] = [ScanJobState.Fitting, ScanJobState.Assembly, ScanJobState.Completed]
        return cloneableStates.includes(scanJob.state) && this.$can().create.scanJob()
    })

    readonly permission = inject(PermissionsService)
    readonly scanJobsForTextureSet = inject(ScanJobsForTextureSetGQL)
    readonly scanJobUpdate = inject(ScanJobUpdateGQL)
    private readonly cloneScanJobGql = inject(CloneScanJobForRefittingGQL)
    private readonly notifications = inject(NotificationsService)
    private readonly refreshService = inject(RefreshService)

    $can = this.permission.$to
    private readonly dialog = inject(MatDialog)

    ngOnInit() {
        const textureSetId = this.$textureSetId()
        if (textureSetId) {
            fetchThrowingErrors(this.scanJobsForTextureSet)({id: textureSetId}).then((data) => {
                this.$scanJob.set(data.textureSet?.scanJob ?? undefined)
            })
        }
    }

    downloadLog(downloadUrl: string): void {
        FilesService.downloadFile("log.txt", downloadUrl)
    }

    updateScanJob(data: Omit<MutationUpdateScanJobInput, "id">) {
        const scanJobId = this.$scanJob()?.id
        if (scanJobId) {
            mutateThrowingErrors(this.scanJobUpdate)({input: {id: scanJobId, ...data}}).then(({updateScanJob}) => {
                this.$scanJob.set(updateScanJob)
            })
        }
    }

    showScanJobDialog(scanJob: ScanJobFragment): void {
        this.dialog.open(ScanJobDialogComponent, {
            panelClass: "cm-no-padding-dialog",
            width: "1700px",
            data: {
                scanJobId: scanJob.id,
            },
        })
    }

    readonly $cloning = signal(false)

    cloneForRefitting(fittingSchema: FittingSchema): void {
        const scanJob = this.$scanJob()
        if (!scanJob) {
            return
        }
        this.$cloning.set(true)
        void this.notifications
            .withUserFeedback(
                async () => {
                    await mutateThrowingErrors(this.cloneScanJobGql)({input: {scanJobId: scanJob.id, fittingSchema}})
                    this.refreshService.item({id: scanJob.textureSet.textureGroup.id, __typename: "TextureGroup"})
                },
                {
                    success: "Scan job cloned for refitting",
                    error: "Failed to clone scan job",
                },
            )
            .finally(() => this.$cloning.set(false))
    }

    protected readonly Enums = Enums
    protected readonly FittingSchema = FittingSchema
    protected readonly Labels = Labels
}
